/**
 * RTCSignalingManager.js
 * 
 * DUAL-MODE SIGNALLING bróker:
 * 1. Supabase Realtime: pure serverless broker (perfect for Netlify)
 * 2. Native WebSocket: fallback for local testing
 */
import { createClient } from '@supabase/supabase-js';

export class RTCSignalingManager {
  constructor() {
    this.socket = null; // WS mode
    this.supabase = null; // Supabase mode
    this.channel = null; // Supabase mode
    
    this.serverUrl = '';
    this.peerId = null;
    this.nickname = '';
    this.currentRoom = null;
    this.isConnected = false;
    this.mode = 'websocket'; // 'supabase' or 'websocket'

    // Callbacks registered by MultiplayerManager
    this.onConnectCallback = null;
    this.onDisconnectCallback = null;
    this.onPeerJoinedCallback = null;
    this.onPeerLeftCallback = null;
    this.onSignalCallback = null;
    this.onErrorCallback = null;

    this.reconnectTimer = null;
    this.isIntentionallyClosed = false;
  }

  /**
   * Connects to the signaling bróker. Automatically selects Supabase Realtime
   * if env credentials are configured, else falls back to WebSocket.
   */
  connect(serverUrl, peerId, nickname) {
    this.serverUrl = serverUrl;
    this.peerId = peerId;
    this.nickname = nickname;
    this.isIntentionallyClosed = false;

    // Disconnect previous connections if any
    this.close();

    // Check if Supabase settings are present and not template defaults
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const hasSupabase = supabaseUrl && supabaseUrl !== 'YOUR_SUPABASE_PROJECT_URL' && supabaseUrl.trim() !== '' &&
                        supabaseKey && supabaseKey !== 'YOUR_SUPABASE_ANON_PUBLIC_KEY' && supabaseKey.trim() !== '';

    if (hasSupabase) {
      this.initSupabase(supabaseUrl, supabaseKey);
    } else {
      this.initWebSocket(serverUrl);
    }
  }

  /* =========================================================================
   * SUPABASE REALTIME MODE
   * ========================================================================= */

  initSupabase(url, key) {
    this.mode = 'supabase';
    console.log('[RTCSignalingManager] Connecting in serverless SUPABASE Realtime mode...');

    try {
      this.supabase = createClient(url, key, {
        auth: { persistSession: false }
      });
      this.isConnected = true;
      
      if (this.onConnectCallback) this.onConnectCallback();

      // Rejoin room if one was already active
      if (this.currentRoom) {
        this.joinSupabaseRoom(this.currentRoom);
      }
    } catch (err) {
      console.error('[RTCSignalingManager] Supabase connection failed:', err);
      this.handleError(err);
    }
  }

  joinSupabaseRoom(roomName) {
    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }

    console.log(`[RTCSignalingManager] [Supabase] Subscribing to channel: ${roomName}`);
    
    this.channel = this.supabase.channel(roomName, {
      config: {
        presence: {
          key: this.peerId,
        }
      }
    });

    this.channel
      .on('presence', { event: 'sync' }, () => {
        const state = this.channel.presenceState();
        
        // Scan all current room occupants
        for (const [key, presences] of Object.entries(state)) {
          if (key !== this.peerId && presences && presences[0]) {
            const remoteNickname = presences[0].nickname || 'Explorer';
            
            // To prevent BOTH clients from initiating a WebRTC offer at the same time
            // we compare peerIds lexicographically. The one with the smaller ID initiates.
            const initiate = this.peerId < key;
            if (this.onPeerJoinedCallback) {
              this.onPeerJoinedCallback(key, remoteNickname, initiate);
            }
          }
        }
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        if (key !== this.peerId && newPresences && newPresences[0]) {
          const remoteNickname = newPresences[0].nickname || 'Explorer';
          console.log(`[RTCSignalingManager] [Supabase] Presence Joined: ${remoteNickname} (${key})`);
          
          const initiate = this.peerId < key;
          if (this.onPeerJoinedCallback) {
            this.onPeerJoinedCallback(key, remoteNickname, initiate);
          }
        }
      })
      .on('presence', { event: 'leave' }, ({ key }) => {
        if (key !== this.peerId) {
          console.log(`[RTCSignalingManager] [Supabase] Presence Left: ${key}`);
          if (this.onPeerLeftCallback) {
            this.onPeerLeftCallback(key);
          }
        }
      })
      .on('broadcast', { event: 'signal' }, ({ payload }) => {
        if (payload.recipient === this.peerId && this.onSignalCallback) {
          this.onSignalCallback(payload.sender, payload.data);
        }
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[RTCSignalingManager] [Supabase] Realtime Channel active. Tracking Presence.');
          await this.channel.track({ nickname: this.nickname });
        }
      });
  }

  sendSupabaseSignal(recipientId, signalData) {
    if (!this.channel) return;

    this.channel.send({
      type: 'broadcast',
      event: 'signal',
      payload: {
        sender: this.peerId,
        recipient: recipientId,
        data: signalData
      }
    }).catch(err => console.error('[RTCSignalingManager] Supabase Broadcast failed:', err));
  }

  /* =========================================================================
   * NATIVE WEBSOCKET FALLBACK MODE
   * ========================================================================= */

  initWebSocket(url) {
    this.mode = 'websocket';
    console.log('[RTCSignalingManager] Connecting in native WEBSOCKET fallback mode...');

    try {
      this.socket = new WebSocket(url);
      this.setupSocketListeners();
    } catch (err) {
      console.error('[RTCSignalingManager] WebSocket connection failed:', err);
      this.handleError(err);
    }
  }

  setupSocketListeners() {
    this.socket.onopen = () => {
      console.log('[RTCSignalingManager] [WebSocket] Connected.');
      this.isConnected = true;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (this.onConnectCallback) this.onConnectCallback();

      if (this.currentRoom) {
        this.sendWS({
          type: 'join',
          room: this.currentRoom,
          peerId: this.peerId,
          nickname: this.nickname
        });
      }
    };

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        this.handleWSMessage(message);
      } catch (err) {
        console.warn('[RTCSignalingManager] [WebSocket] Failed to parse message:', err);
      }
    };

    this.socket.onclose = (event) => {
      console.log(`[RTCSignalingManager] [WebSocket] Closed (code: ${event.code}).`);
      this.isConnected = false;
      if (this.onDisconnectCallback) this.onDisconnectCallback();

      // Auto reconnect
      if (!this.isIntentionallyClosed && !this.reconnectTimer) {
        this.reconnectTimer = setTimeout(() => {
          this.reconnectTimer = null;
          this.connect(this.serverUrl, this.peerId, this.nickname);
        }, 3000);
      }
    };

    this.socket.onerror = (err) => {
      this.handleError(err);
    };
  }

  handleWSMessage(message) {
    switch (message.type) {
      case 'joined-peers':
        if (message.peers) {
          message.peers.forEach(peer => {
            if (peer.id !== this.peerId && this.onPeerJoinedCallback) {
              this.onPeerJoinedCallback(peer.id, peer.nickname, true);
            }
          });
        }
        break;

      case 'peer-joined':
        if (message.peerId !== this.peerId && this.onPeerJoinedCallback) {
          this.onPeerJoinedCallback(message.peerId, message.nickname, false);
        }
        break;

      case 'peer-left':
        if (this.onPeerLeftCallback) {
          this.onPeerLeftCallback(message.peerId);
        }
        break;

      case 'signal':
        if (message.recipient === this.peerId && this.onSignalCallback) {
          this.onSignalCallback(message.sender, message.data);
        }
        break;
    }
  }

  sendWS(payload) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  /* =========================================================================
   * UNIFIED API INTERFACES
   * ========================================================================= */

  joinRoom(roomX, roomZ) {
    const roomName = `room_${roomX}_${roomZ}`;
    if (this.currentRoom === roomName) return;

    this.currentRoom = roomName;

    if (this.mode === 'supabase') {
      if (this.isConnected) this.joinSupabaseRoom(roomName);
    } else {
      if (this.isConnected) {
        this.sendWS({
          type: 'join',
          room: roomName,
          peerId: this.peerId,
          nickname: this.nickname
        });
      }
    }
  }

  leaveRoom() {
    if (!this.currentRoom) return;

    if (this.mode === 'supabase') {
      if (this.channel) {
        this.channel.unsubscribe();
        this.channel = null;
      }
    } else {
      if (this.isConnected) {
        this.sendWS({
          type: 'leave',
          room: this.currentRoom,
          peerId: this.peerId
        });
      }
    }
    this.currentRoom = null;
  }

  sendSignal(recipientId, signalData) {
    if (!this.isConnected) return;

    if (this.mode === 'supabase') {
      this.sendSupabaseSignal(recipientId, signalData);
    } else {
      this.sendWS({
        type: 'signal',
        sender: this.peerId,
        recipient: recipientId,
        data: signalData
      });
    }
  }

  handleError(err) {
    if (this.onErrorCallback) {
      this.onErrorCallback(err);
    }
  }

  close() {
    this.isIntentionallyClosed = true;
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Purgar WebSocket
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    // Purgar Supabase Channel
    if (this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }

    this.supabase = null;
    this.isConnected = false;
    this.currentRoom = null;
  }
}
